import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { ArcElement, BarElement, CategoryScale, Chart, Filler, Legend, LineElement, LinearScale, PointElement, RadialLinearScale, Tooltip } from 'chart.js';
import { RadarChart } from './charts/RadarChart';
import colorLib from '@kurkle/color';
import { surveyResultData } from "../data/surveyResultData.js";
import { summarizedSurveyResultData } from "../data/summarizedSurveyResultData.js";
import { ListGroupItemEllipsis } from "./controls/ListGroupItemEllipsis.js";
import GroupMetrics from "./GroupMetrics.js";
import QuestionMetrics from "./QuestionMetrics.js";
import { SurveyResultsConfig } from "./SurveyResultsConfig.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from "@fortawesome/free-regular-svg-icons";
import { faCircleCheck, faCircleMinus } from '@fortawesome/free-solid-svg-icons';
import { ListGroup, Offcanvas, Form, Container, Row, Col, Dropdown, DropdownMenu } from "react-bootstrap";
import OverallMetrics from "./OverallMetrics.js";
import resolveSurvey from "../data/surveyResolver.js";
import { fetchAuthorized } from '../utils/fetchAuthorized';
import { useMsal } from '@azure/msal-react';

Chart.register(LineElement, PointElement, RadialLinearScale, Filler, Tooltip, Legend, LinearScale, ArcElement, CategoryScale, BarElement);

const COLORS = [
  "#3e2723", // Dark Brown
  "#2962ff", // Vivid Blue
  '#ff0000', // Red
  '#008900', // Green
  '#ff13F0', // Pink
  '#8B4513', // Saddle Brown
  '#8a00c4', // Purple
  "#546e7a", // Dark Slate Gray
  '#ff8000', // Orange
  '#A52A2A', // Brown

  "#3cb371", // Medium Sea Green
  '#89023E',
  '#0000ff', // Blue
  '#000000', // Black
  '#ff6666',
  "#9e9e9e", // Light Gray
  "#4a235a", // Dark Purple
  "#26a69a", // Teal - Darker
  "#004d40",  // Dark Emerald Green
  "#6a5acd", // Blue Violet
  "#55a630", // Olive Green - brighter
  "#663399", // Amethyst
  "#7b68ee", // Lavender
  '#545454',
  "#3c78d8", // Bright Cerulean
  "#2e8b57", // Sea Green
  "#48bb78", // Emerald Green - slightly muted
  "#45ada8", // Teal
  "#2e86de", // Sky Blue
  "#32cd32", // Lime Green
  "#78909c", // Gray
  "#b0bec5", // Cool Gray
  "#4682b4", // Steel Blue
  '#537bc4',
  '#8549ba',
  '#acc236',
  '#166a8f',
  '#00a950',
  '#D88C9A',
  "#5d9bcf", // Sky Blue - slightly desaturated
  '#16697A',
  '#99C1B9',
  '#30343F',
  '#8E7DBE',
  "#3399ff", // Light Blue
  '#fd3459',
  '#58355E',
  '#5998C5',
  '#621B00',
  '#945600',
  '#C75000'
];

const colorPalette1 = {
  red: '#ff0000',
  orange: '#ffa700',
  yellow: '#fff400',
  green: '#2cba00',
  darkGreen: '#a3ff00',
  blue: '#0077c0',
  purple: '#9966ff',
}

const colorPalette2 = {
  red: "#cc0000",
  orange: "#e69138",
  yellow: "#f1c232",
  green: "#6aa84f",
  darkGreen: "#86bb6f",
  blue: "#3d85c6",
  purple: "#8d4fa8",
}

const colorPalette3 = {
  red: "#ff0000",
  orange: "#ff7c00",
  yellow: "#ffec00",
  green: "#2c8f00",
  darkGreen: "#44dc00",
  blue: "#004899",
  purple: "#63008f",
}

const colorPalette4 = {
  red: "#ed2938",
  orange: "#ff8c01",
  yellow: "#ffe733",
  green: "#006b3e",
  darkGreen: "#44dc00",
  blue: "#004899",
  purple: "#63008f",
}

const colorPalette5 = {
  red: "#fd3f46",
  orange: "#ff8b00",
  yellow: "#ffe77a",
  green: "#76d136",
  darkGreen: "#b7eb63",
  blue: "#004899",
  purple: "#63008f",
}

const colorPalette6 = {
  red: "#ff4c4c",
  orange: "#ffc14c",
  yellow: "#fff74c",
  green: "#6bcf4c",
  darkGreen: "#a6e293",
  blue: "#4c6bcf",
  purple: "#b04ccf",
}

const colorPalette7 = {
  red: "#f33d5a",
  orange: "#ffc14c",
  yellow: "#fff74c",
  green: "#5af33d",
  darkGreen: "#a6e293",
  blue: "#3d5af3",
  purple: "#b04ccf",
}

const colorPalette8 = {
  red: "#f94449", //"#f33d5a",
  redOrange: "#fc843e", //"#fb7579",
  orange: "#ffc333", //"#ffaa33",
  orangeYellow: "#fddf33", //"#f99a44",
  yellow: "#fafa33",
  yellowGreen: "#b3e540", //"#f9f544",
  green: "#6bcf4c",
  darkGreen: "#66c266", //"#5af33d", //#a6e293
  greenBlue: "#5495A0",
  blue: "#3d5af3",
  purple: "#b04ccf",
}

// "#f9554e", // Fiery Red
//   "#f27a5b", // Red-Orange
//   "#eaa869", // Orange
//   "#e2c653", // Golden Yellow
//   "#d9e569", // Lime Yellow
//   "#66c266", // Medium Sea Green
//   "#3cb371"  // Bright Green

const colorPalette9 = {
  red: "#f9554e",
  redOrange: "#fd9d65",
  orange: "#ffcf5c",
  orangeYellow: "#fde55c",
  yellow: "#fbfb5c",
  yellowGreen: "#c2ea66",
  green: "#89d970",
  darkGreen: "#66c266",
  greenBlue: "#5495A0",
  blue: "#3d5af3",
  purple: "#b04ccf",
}

const colors = colorPalette9;

function color(index) {
  return COLORS[index % COLORS.length];
}

function transparentize(value, opacity) {
  //return value;
  const alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return colorLib(value).alpha(alpha).rgbString();
}

function getColorFromScore(rank) {
  if (rank <= 1.5)
    return colors.red;
  if (rank > 1.5 && rank <= 2)
    return colors.redOrange;
  if (rank > 2 && rank <= 2.5)
      return colors.orange;
  if (rank > 2.5 && rank <= 3)
    return colors.orangeYellow;
  if (rank > 3 && rank <= 3.5)
    return colors.yellow;
  if(rank > 3.5 && rank <= 4)
    return colors.yellowGreen;
  if(rank > 4 && rank <= 4.5)
    return colors.green;
  if (rank > 4.5)
    return colors.darkGreen;
};

function getColorFromAlignmentRank(rank) {
  if (rank <= .25)
    return colors.darkGreen;
  if (rank > .25 && rank <= .5)
    return colors.green;
  if (rank > .5 && rank <= .75)
      return colors.yellowGreen;
  if (rank > .75 && rank <= 1)
    return colors.yellow;
  if (rank > 1 && rank <= 1.25)
    return colors.orangeYellow;
  if(rank > 1.25 && rank <= 1.5)
    return colors.orange;
  if(rank > 1.5 && rank <= 1.75)
    return colors.redOrange;
  if (rank > 1.75)
    return colors.red;
};

function getClassFromScore(rank) {
  if (rank <= 1.5)
    return "bg-red";
  if (rank > 1.5 && rank <= 2)
    return "bg-redOrange";
  if (rank > 2 && rank <= 2.5)
      return "bg-orange";
  if (rank > 2.5 && rank <= 3)
    return "bg-orangeYellow";
  if (rank > 3 && rank <= 3.5)
    return "bg-yellow";
  if (rank > 3.5 && rank <= 4)
    return "bg-yellowGreen";
  if (rank > 4 && rank <= 4.5)
    return "bg-green";
  if (rank > 4.5)
    return "bg-lightGreen";
};

function getClassFromAlignment(rank) {
  if (rank <= .25)
    return "bg-lightGreen";
  if (rank > .25 && rank <= .5)
    return "bg-green";
  if (rank > .5 && rank <= .75)
      return "bg-yellowGreen";
  if (rank > .75 && rank <= 1)
    return "bg-yellow";
  if (rank > 1 && rank <= 1.25)
    return "bg-orangeYellow";
  if (rank > 1.25 && rank <= 1.5)
    return "bg-orange";
  if (rank > 1.5 && rank <= 1.75)
    return "bg-redOrange";
  if (rank > 1.75)
    return "bg-red";
};

const getQuestionColorCounts = (scores) => {
  const values = { red: 0, redOrange: 0, orange: 0, orangeYellow: 0, yellow: 0, yellowGreen: 0, green: 0, lightGreen: 0 };
  scores.forEach(a => {
    if (a <= 1.5)
      values.red += 1;
    else if (a > 1.5 && a <= 2)
      values.redOrange += 1;
    else if (a > 2 && a <= 2.5)
      values.orange += 1;
    else if (a > 2.5 && a <= 3)
      values.orangeYellow += 1;
    else if (a > 3 && a <= 3.5)
      values.yellow += 1;
    else if (a > 3.5 && a <= 4)
      values.yellowGreen += 1;
    else if (a > 4 && a <= 4.5)
      values.green += 1;
    else if (a > 4.5)
      values.lightGreen += 1;
  });
  return values;
}

const getQuestionAlignmentColorCounts = (alignmentValues) => {
  const values = { red: 0, redOrange: 0, orange: 0, orangeYellow: 0, yellow: 0, yellowGreen: 0, green: 0, lightGreen: 0 };
  alignmentValues.forEach(a => {
    if (a <= .25)
      values.lightGreen += 1;
    else if (a > .25 && a <= .5)
      values.green += 1;
    else if (a > .5 && a <= .75)
      values.yellowGreen += 1;
    else if (a > .75 && a <= 1)
      values.yellow += 1;
    else if (a > 1 && a <= 1.25)
      values.orangeYellow += 1;
    else if (a > 1.25 && a <= 1.5)
      values.orange += 1;
    else if (a > 1.5 && a <= 1.75)
      values.redOrange += 1;
    else if (a > 1.75)
      values.red += 1;
  });
  return values;
}

const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  blue: 'rgb(54, 162, 235)',
  purple: 'rgb(153, 102, 255)',
  grey: 'rgb(201, 203, 207)'
};

const NAMED_COLORS = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.yellow,
  CHART_COLORS.green,
  CHART_COLORS.blue,
  CHART_COLORS.purple,
  CHART_COLORS.grey,
];

const summaryRadarData = {
  labels: ["Strategy", "Work Process", "Structure & Governance", "Information & Metrics", "People & Rewards", "Continuous Alignment", "Leadership & Culture", "Organization Alignment", "Guiding Transformation"],
  datasets: [
    // {
    //   label: "Average Scores",
    //   data: summarizedSurveyResultData.map(a => a.average),
    //   borderColor: CHART_COLORS.green,
    //   backgroundColor: transparentize(CHART_COLORS.green),
    //   fill: 'none',
    //   parsing: {
    //     // xAxisKey: 'questionId',
    //     rAxisKey: 'alignmentRank',
    //   }
    // },
    {
      label: "Alignment Rank",
      data: summarizedSurveyResultData.map(a => a.alignmentRank),
      borderColor: '#545454', //'#0077c0',
      backgroundColor: transparentize('#545454'), //transparentize('#0077c0'),
      fill: 'none',
      parsing: {
        // xAxisKey: 'questionId',
        rAxis: 'alignmentRank',
      }
    },
    {
      data: summarizedSurveyResultData.map(a => 2.9), //[2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9], //[1, 1, 1, 1, 1, 1, 1, 1, 1],
      fill: true, //'origin',
      backgroundColor: transparentize(colors.red, 0.2),
      // backgroundColor: ({chart: {ctx}}) => {
      //   const gradient = ctx.createLinearGradient(0, 0, 0, 20);
      //   gradient.addColorStop(0, 'rgba(250,174,50,1)');   
      //   gradient.addColorStop(1, 'rgba(250,174,50,0)');
      //   return gradient;
      // },
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: summarizedSurveyResultData.map(a => 3.2), //[3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2], //[2, 2, 2, 2, 2, 2, 2, 2, 2],
      fill: '-1',
      backgroundColor: transparentize(colors.orange, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: summarizedSurveyResultData.map(a => 3.4), //[3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4], //[3, 3, 3, 3, 3, 3, 3, 3, 3],
      fill: '-1',
      backgroundColor: transparentize(colors.yellow, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    // {
    //   data: [4, 4, 4, 4, 4, 4, 4, 4, 4],
    //   fill: '-1',
    //   backgroundColor: transparentize(colors.lightGreen, 0.2),
    //   pointRadius: 0,
    //   borderWidth: 0,
    //   borderColor: 'white',
    // },
    {
      data: summarizedSurveyResultData.map(a => 3.6), //[5, 5, 5, 5, 5, 5, 5, 5, 5],
      fill: '-1',
      backgroundColor: transparentize(colors.green, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ],
};

const summaryAverageRadarData = {
  labels: ["Strategy", "Work Process", "Structure & Governance", "Information & Metrics", "People & Rewards", "Continuous Alignment", "Leadership & Culture", "Organization Alignment", "Guiding Transformation"],
  datasets: [
    {
      label: "Average Scores",
      data: summarizedSurveyResultData.map(a => a.average),
      borderColor: '#545454', //'#0077c0',
      backgroundColor: transparentize('#545454'), //transparentize('#0077c0'),
      fill: 'none',
      parsing: {
        // xAxisKey: 'questionId',
        rAxisKey: 'average',
      }
    },
    {
      data: summarizedSurveyResultData.map(a => 2.9), //[2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9], //[1, 1, 1, 1, 1, 1, 1, 1, 1],
      fill: true, //'origin',
      backgroundColor: transparentize(colors.red, 0.2),
      // backgroundColor: ({chart: {ctx}}) => {
      //   const gradient = ctx.createLinearGradient(0, 0, 0, 20);
      //   gradient.addColorStop(0, 'rgba(250,174,50,1)');   
      //   gradient.addColorStop(1, 'rgba(250,174,50,0)');
      //   return gradient;
      // },
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: summarizedSurveyResultData.map(a => 3.2), //[3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2], //[2, 2, 2, 2, 2, 2, 2, 2, 2],
      fill: '-1',
      backgroundColor: transparentize(colors.orange, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: summarizedSurveyResultData.map(a => 3.4), //[3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4], //[3, 3, 3, 3, 3, 3, 3, 3, 3],
      fill: '-1',
      backgroundColor: transparentize(colors.yellow, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    // {
    //   data: [4, 4, 4, 4, 4, 4, 4, 4, 4],
    //   fill: '-1',
    //   backgroundColor: transparentize(colors.lightGreen, 0.2),
    //   pointRadius: 0,
    //   borderWidth: 0,
    //   borderColor: 'white',
    // },
    {
      data: summarizedSurveyResultData.map(a => 3.6), //[5, 5, 5, 5, 5, 5, 5, 5, 5],
      fill: '-1',
      backgroundColor: transparentize(colors.green, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ],
};

const questionAverageRadarData = {
  labels: surveyResultData.map(a => ("Q" + a.questionId)),
  datasets: [
    {
      label: "Average Scores",
      data: surveyResultData.map(a => a.average),
      borderColor: '#545454', //'#0077c0',
      backgroundColor: transparentize('#545454'), //'#0077c0'),
      fill: 'none',
      parsing: {
        // xAxisKey: 'questionId',
        rAxisKey: 'average',
      }
    },
    {
      data: surveyResultData.map(a => 2.9), //[2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9,2.9], //[1, 1, 1, 1, 1, 1, 1, 1, 1],
      fill: true, //'origin',
      backgroundColor: transparentize(colors.red, 0.2),
      // backgroundColor: ({chart: {ctx}}) => {
      //   const gradient = ctx.createLinearGradient(0, 0, 0, 20);
      //   gradient.addColorStop(0, 'rgba(250,174,50,1)');   
      //   gradient.addColorStop(1, 'rgba(250,174,50,0)');
      //   return gradient;
      // },
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: surveyResultData.map(a => 3.2), //[3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2,3.2], //[2, 2, 2, 2, 2, 2, 2, 2, 2],
      fill: '-1',
      backgroundColor: transparentize(colors.orange, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: surveyResultData.map(a => 3.4), //[3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4, 3.4], //[3, 3, 3, 3, 3, 3, 3, 3, 3],
      fill: '-1',
      backgroundColor: transparentize(colors.yellow, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    // {
    //   data: [4, 4, 4, 4, 4, 4, 4, 4, 4],
    //   fill: '-1',
    //   backgroundColor: transparentize(colors.lightGreen, 0.2),
    //   pointRadius: 0,
    //   borderWidth: 0,
    //   borderColor: 'white',
    // },
    {
      data: surveyResultData.map(a => 3.6), //[5, 5, 5, 5, 5, 5, 5, 5, 5],
      fill: '-1',
      backgroundColor: transparentize(colors.green, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ],
};

const pieData = {
  labels: ["Strategy", "Work Process", "Structure & Governance", "Information & Metrics", "People & Rewards", "Continuous Alignment", "Leadership & Culture", "Organization Alignment", "Guiding Transformation"],
  datasets: [
    {
      label: "Alignment Rank",
      data: summarizedSurveyResultData.map(a => a.alignmentRank),
      hoverOffset: 30,
      borderWidth: 1,
      backgroundColor: [
        '#0077c0',
        '#ff0000',
        '#ffa700',
        '#fff400',
        '#a3ff00',
        '#2cba00',
        'orange',
        'blue',
        'purple'
      ]
    },
  ],
};

const barData = {
  labels: surveyResultData.map(a => ("Q" + a.questionId)),
  datasets: [
    {
      label: "Alignment Rank",
      data: surveyResultData.map(a => a.alignmentRank),
      hoverOffset: 30,
      borderWidth: 1,
      backgroundColor: colors.orange, //summarizedSurveyResultData.map(a => getColorFromAlignmentRank(a.alignmentRank))
    },
    {
      label: "Average",
      data: surveyResultData.map(a => a.average),
      hoverOffset: 30,
      borderWidth: 1,
      backgroundColor: colors.blue, //summarizedSurveyResultData.map(a => getColorFromAlignmentRank(a.alignmentRank))
    },
  ],
};
//summarizedSurveyResultData.map(a => getColorFromAlignmentRank(a.alignmentRank))

const scatterData = {
  datasets: [
    {
      label: 'Alignment Rank',
      backgroundColor: '#fd7702',
      data: surveyResultData.map(({ questionId, alignmentRank }) => ({ ['x']: questionId, ['y']: alignmentRank })),
    },
    {
      label: 'Average Score',
      backgroundColor: '#003366',
      data: surveyResultData.map(({ questionId, average }) => ({ ['x']: questionId, ['y']: average })),
    }
  ]
}

const lineData = {
  labels: surveyResultData.map(a => ("Q" + a.questionId)),
  datasets: [
    {
      label: 'Alignment Rank',
      borderColor: '#fd7702', //colors.orange, //'#0077c0',
      data: surveyResultData.map(a => a.alignmentRank),
    },
    {
      label: 'Average Score',
      borderColor: '#003366', //colors.blue, //'#545454',
      data: surveyResultData.map(a => a.average),
    }
  ]
}

const areaData = {
  labels: surveyResultData.map(a => ("Q" + a.questionId)),
  datasets: [
    {
      label: 'Alignment Rank',
      backgroundColor: transparentize('#b3cde0', .2), //'#0077c0', .2),
      fill: true,
      pointRadius: 0,
      data: surveyResultData.map(({ questionId, alignmentRank }) => ({ ['x']: questionId, ['y']: alignmentRank })),
    },
    {
      label: 'Average Score',
      backgroundColor: transparentize('#005b96', .2), //'#545454', .2),
      fill: true,
      pointRadius: 0,
      data: surveyResultData.map(({ questionId, average }) => ({ ['x']: questionId, ['y']: average })),
    }
  ]
}

function roundNumber(data, type, row) {
  return (Math.round(data * 100) / 100);
}

const columns = [
  { data: "questionId", title: "Question #" },
  { data: "questionGroup", title: "Group" },
  { data: "average", title: "Average Score", render: roundNumber },
  { data: "alignmentRank", title: "Alignment Rank", render: roundNumber },
]
const dataTableConfig = {
  data: surveyResultData,
  columns: columns,
  searching: false,
  paging: false,
  select: true,
}

const summaryColumns = [
  { data: "questionGroup", title: "Group" },
  { data: "average", title: "Average Score", render: roundNumber },
  { data: "alignmentRank", title: "Alignment Rank", render: roundNumber },
]

const summaryDataTableConfig = {
  data: summarizedSurveyResultData,
  columns: summaryColumns,
  searching: false,
  paging: false,
  select: true,
}



function scaleAlignmentIndex(score) {
  const roundedScore = Math.round(score * 10) / 10;
  if (roundedScore <= 2.9)
    return roundedScore / 2.9;
  if (roundedScore <= 3.2)
    return (roundedScore - 2.9) / 0.3 + 1;
  if (roundedScore <= 3.4)
    return (roundedScore - 3.2) / 0.2 + 2;
  if (roundedScore <= 4)
    return (roundedScore - 3.4) / 0.6 + 3;
  return roundedScore;
}

function scaleAverage(score) {
  return score;
  const roundedScore = Math.round(score * 10) / 10;
  if (roundedScore <= 2.9)
    return roundedScore / 2.9;
  if (roundedScore <= 3.2)
    return (roundedScore - 2.9) / 0.3 + 1;
  if (roundedScore <= 3.4)
    return (roundedScore - 3.2) / 0.2 + 2;
  if (roundedScore <= 4)
    return (roundedScore - 3.4) / 1.6 + 3;
  return roundedScore;
}

const round = (value) => Math.round(value * 10) / 10;

export default function SurveyResults(props) {
  const { clientId, surveyId } = useParams();
  const { instance } = useMsal();
  // const { state } = useContext(AppContext);
  // const client = state.clients[match.params.id];
  const [surveyMetric, setSurveyMetric] = useState();
  const [clientName, setClientName] = useState();
  const [questionMetrics, setQuestionMetrics] = useState([]);
  const [questionMetric, setQuestionMetric] = useState();
  const [commentMetrics, setCommentMetrics] = useState([]);
  const [commentMetric, setCommentMetric] = useState();
  const [questionTitle, setQuestionTitle] = useState();
  const [questionGroupMetrics, setQuestionGroupMetrics] = useState([]);
  const [questionGroupMetric, setQuestionGroupMetric] = useState();
  const [allQuestionGroupMetrics, setAllQuestionGroupMetrics] = useState([]);
  const [questionColorCounts, setQuestionColorCounts] = useState({ red: 0, orange: 0, yellow: 0, green: 0 });
  const [questionAlignmentColorCounts, setQuestionAlignmentColorCounts] = useState({ red: 0, orange: 0, yellow: 0, green: 0 });
  const [questionGroupColorCounts, setQuestionGroupColorCounts] = useState({ red: 0, orange: 0, yellow: 0, green: 0 });
  const [pageView, setPageView] = useState("overall"); // overall | group | question
  const [enabledLevels, setEnabledLevels] = useState(["All"]);
  const [showAverageScore, setShowAverageScore] = useState(true);
  const [showDegreeOfAlignment, setShowDegreeOfAlignment] = useState(false);
  const [showLevel, setShowLevel] = useState(true);
  const [showFunction, setShowFunction] = useState(false);
  const [showLevelToggle, setShowLevelToggle] = useState(true);
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [sectionIndexes, setSectionIndexes] = useState([]);

  const [surveyJson, setSurveyJson] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showComments, setShowComments] = useState(false);
  const [commentQuestion, setCommentQuestion] = useState(null);

  // const { config, setConfig } = useContext(SurveyResultsContext);
  // setConfig({ showLevel, toggleShowLevel, showFunction, toggleShowFunction });

  useEffect(() => {
    getQuestionMetrics();
  }, [clientId, surveyId]);

  // useEffect(() => {
  //   if(!loading) {
  //     console.log("not loading and updating question metrics");
  //     init(surveyMetric);
  //   }
  // }, [questionMetrics]);

  const changePageView = (value) => {
    if(value === "overall")
      setQuestionGroupMetric(null);
    setPageView(value);
  }

  const getQuestionMetrics = async () => {
    try {
      const response = await fetchAuthorized(instance, `${process.env.REACT_APP_API_URL}SurveyMetrics/clients/${clientId}/surveys/${surveyId}`);

      if (!response.ok) {
        throw new Error('Could not get survey metrics for client.');
      }
      const surveyMetric = await response.json();
      setSurveyMetric(surveyMetric);
      init(surveyMetric);

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  }

  function init(surveyMetric) {
    setClientName(surveyMetric.clientName);
    const resolvedSurveyJson = resolveSurvey(surveyMetric.surveyType, { team: surveyMetric.team });
    setSurveyJson(resolvedSurveyJson);
    const ratingQuestions = resolvedSurveyJson.pages.flatMap(a => a.elements).filter(a => a.type === "rating");
    setQuestions(ratingQuestions);
    setQuestionTitle(ratingQuestions[0].title);

    const groupQuestions = resolvedSurveyJson.pages.map(a => { 
      const questions = a.elements.filter(a => a.type === "rating");
      const firstQuestionId = parseInt(questions[0].questionId);
      const lastQuestionId = parseInt(questions.at(-1).questionId);
      const middleQuestionId = Math.ceil((firstQuestionId + lastQuestionId) / 2);
      return { name: a.name, first: firstQuestionId, middle: middleQuestionId, last: lastQuestionId }; 
    });
    setGroupQuestions(groupQuestions);

    const indexes = groupQuestions.map((a, i) => a.last - i);
    indexes.unshift(0);
    indexes.pop();
    setSectionIndexes(indexes);
    
    const levelRegex = /^L\d+\+*$/
    const questionGroups = resolvedSurveyJson.pages.map(a => a.name);
    const allQuestionGroupMetrics = getQuestionGroupMetrics(surveyMetric.questionMetrics, questionGroups);
    const metricScoreDistinctNames = [...new Set(surveyMetric.questionMetrics[0].metricScores.map(a => a.name))];
    if(metricScoreDistinctNames.filter(a => a.match(levelRegex)).length < 2)
    {
      setShowLevel(false);
      setShowLevelToggle(false);
      setShowFunction(true);
      setupQuestionMetrics(surveyMetric, allQuestionGroupMetrics, false, true);    
    }
    else
      setupQuestionMetrics(surveyMetric, allQuestionGroupMetrics, showLevel, showFunction);

    setCommentMetrics(surveyMetric.commentMetrics);
    setCommentMetric(surveyMetric.commentMetrics[0]);
  }

  function setupQuestionMetrics(surveyMetric, allQuestionGroupMetrics, showLevelData, showFunctionData) {
    var questionMetrics = surveyMetric.questionMetrics;
    var groupMetrics = allQuestionGroupMetrics;
    setAllQuestionGroupMetrics(allQuestionGroupMetrics);

    if(!showLevelData && !showFunctionData) {
      questionMetrics = surveyMetric.questionMetrics.map(questionMetric => {
          const metricScores = questionMetric.metricScores.filter(b => b.name === "All");
          return {...questionMetric, metricScores: metricScores }; //copy object, don't mutate
      });
      groupMetrics = allQuestionGroupMetrics.map(groupMetric => {
        const metricScores = groupMetric.metricScores.filter(b => b.name === "All");
        return {...groupMetric, metricScores: metricScores }; //copy object, don't mutate
      });
    } else if(showLevelData && !showFunctionData) {
      const levelRegex = /^L\d+\+*$/
      questionMetrics = surveyMetric.questionMetrics.map(questionMetric => {
        const metricScores = questionMetric.metricScores.filter(b => b.name === "All" || b.name.match(levelRegex));
        return {...questionMetric, metricScores: metricScores }; //copy object, don't mutate
      });
      groupMetrics = allQuestionGroupMetrics.map(groupMetric => {
        const metricScores = groupMetric.metricScores.filter(b => b.name === "All" || b.name.match(levelRegex));
        return {...groupMetric, metricScores: metricScores }; //copy object, don't mutate
      });
    } else if(showFunctionData && !showLevelData) {
      const levelRegex = /^L\d+\+*$/
      questionMetrics = surveyMetric.questionMetrics.map(questionMetric => {
          const metricScores = questionMetric.metricScores.filter(b => b.name === "All" || !b.name.match(levelRegex));
          return {...questionMetric, metricScores: metricScores }; //copy object, don't mutate
      });
      groupMetrics = allQuestionGroupMetrics.map(groupMetric => {
        const metricScores = groupMetric.metricScores.filter(b => b.name === "All" || !b.name.match(levelRegex));
        return {...groupMetric, metricScores: metricScores }; //copy object, don't mutate
      });
    }

    setQuestionMetrics(questionMetrics);
    setQuestionMetric(questionMetrics[0]);

    //const groupMetrics = getQuestionGroupMetrics(questionMetrics);
    setQuestionGroupMetrics(groupMetrics);
    setQuestionGroupMetric(prev => prev ? groupMetrics.find(a => a.questionGroup === prev.questionGroup) : groupMetrics[0]);
    const questionColors = getQuestionColorCounts(questionMetrics.map(a => a.metricScores.find(b => b.name === "All").average));
    setQuestionColorCounts(questionColors);
    const questionAlignmentColors = getQuestionAlignmentColorCounts(questionMetrics.map(a => a.metricScores.find(b => b.name === "All").alignmentIndex));
    setQuestionAlignmentColorCounts(questionAlignmentColors);
    const questionGroupColors = getQuestionColorCounts(groupMetrics.map(a => a.metricScores.find(b => b.name === "All").average));
    setQuestionGroupColorCounts(questionGroupColors);
    setEnabledLevels(enabledLevels.filter(a => questionMetrics[0].metricScores.some(b => b.name === a)));
  }

  function toggleShowLevel(checked) {
    setShowLevel(checked);
    setupQuestionMetrics(surveyMetric, allQuestionGroupMetrics, checked, showFunction);
  }

  function toggleShowFunction(checked) {
    setShowFunction(checked);
    setupQuestionMetrics(surveyMetric, allQuestionGroupMetrics, showLevel, checked);
  }

  function getLineWidth(context) {
    if(sectionIndexes.includes(context.index)) {
      // if(questionGroupMetric.questionGroup === "Strategy" && (context.index === 0 || context.index === 8))
      //   return 6;
      return 2;
    }
    return 0;
  }

  function getPointLabelFont(context) {
    if(questionGroupMetric && questionGroupMetric.questionGroup === context.label)
      return { size: 12, weight: "normal", style: "oblique" };
    return { size: 12, weight: "normal" };
  }

  const onPointClick = (index, questionMetric, question) => {
    if(!questionMetric) {
      const questionMetric = questionMetrics[index];
      setQuestionMetric(questionMetric);
      const questionGroupMetric = questionGroupMetrics.find(a => a.questionGroup === questionMetric.questionGroup);
      setQuestionGroupMetric(questionGroupMetric);
      const commentMetric = commentMetrics.find(a => a.questionGroup === questionMetric.questionGroup);
      setCommentMetric(commentMetric);
      setQuestionTitle(questions[index].title);
    }
    else {
      setQuestionMetric(questionMetric);
      const questionGroupMetric = questionGroupMetrics.find(a => a.questionGroup === questionMetric.questionGroup);
      setQuestionGroupMetric(questionGroupMetric);
      const commentMetric = commentMetrics.find(a => a.questionGroup === questionMetric.questionGroup);
      setCommentMetric(commentMetric);
      if(question)
        setQuestionTitle(question.title);
      else
        setQuestionTitle(questions.find(a => questionMetric.questionId == a.questionid).title);
    }
    setPageView("question");
  }

  const onLabelClick = group => {
    const questionGroupMetric = questionGroupMetrics.find(a => a.questionGroup === group);
    setQuestionGroupMetric(questionGroupMetric);
    const commentMetric = commentMetrics.find(a => a.questionGroup === group);
    setCommentMetric(commentMetric);
    setPageView("group");
  }

  const setEnabledLegendItems = enabledLevels => {
    // if(enabledLevels.length > 5) 
    //   enabledLevels = enabledLevels.slice(-5);  //Set levels to the last 5 in the array
    setEnabledLevels(enabledLevels);
  }

  const onCommentsClick = () => {
    const page = surveyJson.pages.find(a => a.name === questionGroupMetric.questionGroup);
    const commentQuestion = page.elements.find(a => a.type === "comment");
    setCommentQuestion(commentQuestion);
    setShowComments(true);
  }

  const onGroupPointClick = index => {
    setQuestionGroupMetric(questionGroupMetrics[index]);
    setPageView("group");
  }

  const getDataSets = (questionMetrics) => {
    if(questionMetrics.length === 0)
      return [];
    
    const dataSets = [];
    let index = 0;
    for(const name of questionMetrics[0].metricScores.map(a => a.name)) {
      let color = COLORS[index];
      const hidden = name !== "All";
      const dataSet = {
        label: name,
        data: questionMetrics.map(a => a.metricScores.find(b => b.name === name).average),
        borderColor: color,
        backgroundColor: transparentize(color),
        fill: 'none',
        hidden: hidden,
        // parsing: {
        //   rAxisKey: 'alignmentRank',
        // }
      };
      dataSets.push(dataSet);
      index++;
    }
    return dataSets;
  }

  const backgroundDataSets = [
    {
      data: questionMetrics.map(a => 1.5), //questionMetrics.map(a => 2.9),
      fill: true, //'origin',
      backgroundColor: colors.red,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 2), //questionMetrics.map(a => 2.9),
      fill: true, //'origin',
      backgroundColor: colors.redOrange,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 2.5), //questionMetrics.map(a => 3.2),
      fill: '-1',
      backgroundColor: colors.orange,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 3), //questionMetrics.map(a => 3.2),
      fill: '-1',
      backgroundColor: colors.orangeYellow,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 3.5), //questionMetrics.map(a => 3.4),
      fill: '-1',
      backgroundColor: colors.yellow,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 4), //questionMetrics.map(a => 3.4),
      fill: '-1',
      backgroundColor: colors.yellowGreen,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    // {
    //   data: questionMetrics.map(a => 4), //questionMetrics.map(a => 3.6),
    //   fill: '-1',
    //   backgroundColor: colors.lightGreen,
    //   pointRadius: 0,
    //   borderWidth: 0,
    //   borderColor: 'white',
    // },
    {
      data: questionMetrics.map(a => 4.5), //questionMetrics.map(a => 3.6),
      fill: '-1',
      backgroundColor: colors.green,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 5), //questionMetrics.map(a => 3.6),
      fill: '-1',
      backgroundColor: colors.darkGreen,
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ];

  const backgroundOldDataSets = [
    {
      data: questionMetrics.map(a => 1.5), //questionMetrics.map(a => 2.9),
      fill: true, //'origin',
      backgroundColor: transparentize(colors.red, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 2.5), //questionMetrics.map(a => 3.2),
      fill: '-1',
      backgroundColor: transparentize(colors.orange, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 3.5), //questionMetrics.map(a => 3.4),
      fill: '-1',
      backgroundColor: transparentize(colors.yellow, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 4.5), //questionMetrics.map(a => 3.6),
      fill: '-1',
      backgroundColor: transparentize(colors.darkGreen, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionMetrics.map(a => 5), //questionMetrics.map(a => 3.6),
      fill: '-1',
      backgroundColor: transparentize(colors.green, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ];

  const getData = (name) => {
    if(questionMetrics.length === 0)
      return;
    if(questionMetrics[0].metricScores.some(a => a.name === name)) {
      // if(name == "AlignmentIndex")
      //   return questionMetrics.flatMap(a => scaleAlignmentIndex(a.metricScores.find(a => a.name === name)));
      return questionMetrics.flatMap(a => a.metricScores.find(b => b.name === name));
    }
  }
  
  const questionRadarData = {
    labels: questionMetrics.map(a => groupQuestions.find(b => b.middle === a.questionId)?.name ),
    datasets: [
      ...getDataSets(questionMetrics),
      //...backgroundOldDataSets
      ...backgroundDataSets
    ],
  };
  
  const questionPolarAreaData = {
    labels: questionMetrics.map(a => `Q${a.questionId}`),
    datasets: [
      ...getDataSets(questionMetrics),
      // {
      //   label: "Alignment Index",
      //   data: questionMetrics.map(a => a.alignmentIndex),
      //   // borderColor: '#545454', //'#0077c0',
      //   backgroundColor: questionMetrics.map(a => getColorFromAlignmentRank(a.alignmentIndex)),
      //   fill: 'none',
      // },
    ],
  };

  const questionAverageRadarData = {
    labels: questionMetrics.map(a => a.questionName),
    datasets: [
      {
        label: "Average Scores",
        data: questionMetrics.map(a => scaleAverage(a.average)),
        borderColor: '#545454', //'#0077c0',
        backgroundColor: transparentize('#545454'), //'#0077c0'),
        fill: 'none',
        parsing: {
          // xAxisKey: 'questionId',
          rAxisKey: 'average',
        }
      },
      ...backgroundDataSets
    ],
  };



  const backgroundGroupDataSets = [
    {
      data: questionGroupMetrics.map(a => 1), //questionMetrics.map(a => 2.9),
      fill: true, //'origin',
      backgroundColor: transparentize(colors.red, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionGroupMetrics.map(a => 2), //questionMetrics.map(a => 3.2),
      fill: '-1',
      backgroundColor: transparentize(colors.orange, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionGroupMetrics.map(a => 3), //questionMetrics.map(a => 3.4),
      fill: '-1',
      backgroundColor: transparentize(colors.yellow, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
    {
      data: questionGroupMetrics.map(a => 4), //questionMetrics.map(a => 3.6),
      fill: '-1',
      backgroundColor: transparentize(colors.green, 0.2),
      pointRadius: 0,
      borderWidth: 0,
      borderColor: 'white',
    },
  ] 

  const getQuestionGroupMetrics = (questionMetrics, questionGroups) => {
    const values = [];
    questionGroups.forEach(groupName => {
      const questions = questionMetrics.filter(a => a.questionGroup === groupName);
      const length = questions.length;

      const metricScoreSums = {};
      const metricAlignmentScoreSums = {};
      const names = questions[0].metricScores.map(a => a.name);
      for(const name of names) {
        metricScoreSums[name] = 0;
        metricAlignmentScoreSums[name] = 0;
      }

      questions.forEach(question => {
        for(const name of names) {
          const metricScore = question.metricScores.find(a => a.name === name);
          metricScoreSums[name] += metricScore.average;
          metricAlignmentScoreSums[name] += metricScore.alignmentIndex;
        }
      });

      let metricScores = [];
      for(const name in metricScoreSums) {
        metricScores.push({
          name: name,
          average: metricScoreSums[name] / length,
          alignmentIndex: metricAlignmentScoreSums[name] / length,
        });
      }

      values.push({
        clientId: clientId,
        surveyId: surveyId,
        questionGroup: groupName,
        metricScores: metricScores
      });
    });
    return values;
  }

  

  const questionGroupRadarData = {
    labels: questionGroupMetrics.map(a => a.questionGroup),
    datasets: [
      ...getDataSets(questionGroupMetrics),
      ...backgroundGroupDataSets
    ],
  };

  const questionGroupPolarAreaData = {
    labels: questionGroupMetrics.map(a => a.questionGroup),
    datasets: [
      ...getDataSets(questionMetrics),
    ],
  };

  const questionDoughnutData = {
    //labels: [ "0 - 2.9", "3 - 3.2", "3.3 - 3.4", "3.5 - 4" ],
    labels: [ "Strong Disagreement", "Moderate Disagreement", "Moderate Agreement", "Strong Agreement" ],
    datasets: [{
      data: [ questionColorCounts.red ?? 0, questionColorCounts.orange ?? 0, questionColorCounts.yellow ?? 0, questionColorCounts.green ?? 0 ],
      backgroundColor: [ colors.red, colors.orange, colors.yellow, colors.green  ]
    }]
  };

  const shadowRadarLinePlugin = {
    id: 'shadowRadarLine',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      ctx.save();

      const originalLineDraw = ctx.stroke;
      ctx.stroke = function () {
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 4;
        ctx.shadowOffsetY = 4;
        originalLineDraw.apply(this, arguments);
        ctx.restore();
      }
    }
  }

  const labelItemsPlugin = {
    id: "labelItems",
    afterDatasetsDraw(chart, args, pluginOptions) {
      const {ctx, data, scales: {r}} = chart;
      const xCenter = r.xCenter;
      const yCenter = r.yCenter;
      const labels = chart.config.data.labels;
      const pointLabelItems = chart.scales.r._pointLabelItems;
      labels.forEach((label, index) => {
        if(!label)
          return;

        const radius = pluginOptions.selectedGroup === label ? 25 : 22;
        const groupQuestion = groupQuestions.find(a => a.name === label);
        const { top, right, bottom, left } = pointLabelItems[index];
        let x = right - ((right - left) / 2);
        let y = top <= yCenter ? top - radius - 5 : bottom + radius + 5;

        // If center is between two points, calculate the center of the two points.
        // if((groupQuestion.last - groupQuestion.first) % 2 !== 0) {
        //   const { top, right, bottom, left } = pointLabelItems[index - 1];
        //   const prevX = right - ((right - left) / 2);
        //   const prevY = top < yCenter ? top - radius - 5 : bottom + radius + 5;
        //   x = (prevX + x) / 2;
        //   y = (prevY + y) / 2;
        // }

        pointLabelItems[index].circleTop = y - radius;
        pointLabelItems[index].circleRight = x + radius;
        pointLabelItems[index].circleBottom = y + radius;
        pointLabelItems[index].circleLeft = x - radius;

        const enabledLegendItems = chart.legend.legendItems.filter(a => !a.hidden).map(a => a.text);
        
        const questionGroupMetric = allQuestionGroupMetrics.find(a => a.questionGroup === label);
        const groupAverage = enabledLegendItems.length === 1 ? round(questionGroupMetric.metricScores.find(a => a.name === enabledLegendItems[0]).average) : round(questionGroupMetric.metricScores.find(a => a.name === "All").average);
        
        ctx.save();
        ctx.translate(x, y);

        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        ctx.fillStyle = getColorFromScore(groupAverage);
        if(pluginOptions.selectedGroup === label) {
          ctx.shadowBlur = 4;
          ctx.shadowColor = "gray";
        }
        ctx.fill();
        //ctx.strokeStyle = "#000000";
        ctx.textAlign = "center";
        ctx.fillStyle = "#545454";
        ctx.shadowBlur = 0;
        ctx.textBaseline = "middle";
        var fontFamily = "'Segoe UI', 'Roboto', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', 'Arial'";
        ctx.font = pluginOptions.selectedGroup === label ? `bold 18px ${fontFamily}` : `normal 18px ${fontFamily}`;
        ctx.fillText(groupAverage.toFixed(1), 0, 0);
        ctx.restore();

        //angle lines
        if(pluginOptions.selectedGroup === label) {
          const labelIndex = labels.filter(a => a).findIndex(a => a === label);
          const datasetMeta = chart.getDatasetMeta(0);
          const radius = r.drawingArea + 1;
          const startIndex = sectionIndexes[labelIndex];
          const startAngle = datasetMeta.data[startIndex].angle;
          const endIndex = labelIndex === sectionIndexes.length - 1 ? 0 : sectionIndexes[labelIndex+1];
          const endAngle = datasetMeta.data[endIndex].angle;
          
          ctx.save();
          ctx.translate(xCenter, yCenter);
          ctx.beginPath();
          ctx.strokeStyle = "rgb(0, 0, 0, .1)";
          ctx.lineWidth = 2;
          ctx.shadowBlur = 8;
          ctx.shadowColor = "black";

          //const startCoordinates = [Math.cos(startAngle) * radius, Math.sin(startAngle) * radius];
          //const endCoordinates = [Math.cos(endAngle) * radius, Math.sin(endAngle) * radius];

          ctx.arc(0, 0, radius, startAngle, endAngle, false);
          ctx.stroke(); //first stroke of just the arc to make it darker

          ctx.strokeStyle = "rgb(0, 0, 0, .1)";
          ctx.lineTo(0,0);
          ctx.closePath();
          ctx.fillStyle = "rgb(255, 255, 255, .1)";
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        }
      });
    }
  }
  
  let midPointQuestionId = 0;
  if(questionMetrics && questionMetrics.length > 0) {
    const midpointIndex = Math.floor(questionMetrics.length / 2);
    const questionId = questionMetrics[midpointIndex].questionId;
    const groupQuestion = groupQuestions.find(a => questionId >= a.first && questionId <= a.last);    
    midPointQuestionId = questionId < groupQuestion.middle ? groupQuestion.first : groupQuestions[groupQuestions.indexOf(groupQuestion) + 1].first;
  }
  const leftQuestions = questionMetrics.filter(a => a.questionId >= midPointQuestionId).reverse();


  if(loading)
    return (
      <div className="h-100 d-flex justify-content-center align-items-center">
        <span className="spinner-border text-primary" style={{width: "4rem", height: "4rem"}} role="status" />
      </div>
    )

  if(error)
    return (<p>Error: {error}</p>)

  let questionGroupAverage = 0;
  if(pageView !== "overall")
    questionGroupAverage = enabledLevels.length === 1 ? round(questionGroupMetric.metricScores.find(a => a.name === enabledLevels[0]).average) : round(questionGroupMetric.metricScores.find(a => a.name === "All").average);
  
  return (
    <Container fluid className="h-100">
      <div className="flex-right pt-1" style={{position: 'absolute', right: 12 }}>
        <SurveyResultsConfig
          showLevelToggle={showLevelToggle}
          showLevel={showLevel}
          toggleShowLevel={toggleShowLevel}
          showFunction={showFunction}
          toggleShowFunction={toggleShowFunction}
          showAverageScore={showAverageScore}
          setShowAverageScore={setShowAverageScore}
          showDegreeOfAlignment={showDegreeOfAlignment}
          setShowDegreeOfAlignment={setShowDegreeOfAlignment}
        />
        </div>
      <Row className="h-100" md={pageView === "overall" ? 12 : 2}>
        { pageView === "overall" &&
          <Col>
            <OverallMetrics
                questionMetrics={leftQuestions}
                questionGroupMetrics={questionGroupMetrics}
                questions={questions}
                enabledLevels={enabledLevels}
                showAverageScore={showAverageScore}
                showDegreeOfAlignment={showDegreeOfAlignment}
                getClassFromScore={getClassFromScore}
                getClassFromAlignment={getClassFromAlignment}
                onQuestionClick={onPointClick}
                onLabelClick={onLabelClick}
                toggleDegreeOfAlignment={() => setShowDegreeOfAlignment(!showDegreeOfAlignment)}
              />
          </Col>
        }
        <Col md={6}>
          <div className="chart-container">
            <RadarChart 
              chartData={questionRadarData} 
              plugins={[labelItemsPlugin, shadowRadarLinePlugin]}
              onPointClick={onPointClick}
              onLabelClick={onLabelClick}
              setEnabledLegendItems={setEnabledLegendItems}
              getLineWidth={getLineWidth}
              getPointLabelFont={getPointLabelFont}
              selectedGroup={questionGroupMetric ? questionGroupMetric.questionGroup : null}
              sectionIndexes={sectionIndexes}
            />
          </div>
        </Col>
        <Col className="mx-auto d-flex">
          <div className="h-100 w-100 mt-2">
            { pageView !== "overall" && (
              <div className="w-100 d-flex">
                <div className="w-100 d-flex flex-fill justify-content-evenly mb-3">
                  <div className="align-center justify-content-evenly">
                    <h2 className="text-center fw-normal">{clientName}</h2>
                    <h3 className="text-center">{questionGroupMetric.questionGroup}</h3>
                  </div>
                  <div className="circle-container">
                      <div className={`circle ${getClassFromScore(questionGroupAverage)}`}>
                          <div>{questionGroupAverage.toFixed(1)}</div>
                      </div>
                  </div>
                </div>
              </div>
            )}
            { pageView === "group" &&
              <GroupMetrics
                questionGroupMetric={questionGroupMetric}
                questionMetrics={questionMetrics}
                questions={questions}
                enabledLevels={enabledLevels}
                questionColorCounts={questionColorCounts}
                questionAlignmentColorCounts={questionAlignmentColorCounts}
                showAverageScore={showAverageScore}
                showDegreeOfAlignment={showDegreeOfAlignment}
                getClassFromScore={getClassFromScore}
                getClassFromAlignment={getClassFromAlignment}
                onQuestionClick={onPointClick}
                setPageView={changePageView}
                colors={colors}
              />
            }
            { pageView === "overall" &&
              <OverallMetrics 
                questionMetrics={questionMetrics.filter(a => a.questionId < midPointQuestionId)}
                questionGroupMetrics={questionGroupMetrics}
                questions={questions}
                enabledLevels={enabledLevels}
                showAverageScore={showAverageScore}
                showDegreeOfAlignment={showDegreeOfAlignment}
                getClassFromScore={getClassFromScore}
                getClassFromAlignment={getClassFromAlignment}
                onQuestionClick={onPointClick}
                onLabelClick={onLabelClick}
                toggleDegreeOfAlignment={() => setShowDegreeOfAlignment(!showDegreeOfAlignment)}
              />
            }
            { pageView === "question" &&
              <QuestionMetrics
                questionTitle={questionTitle}
                questionMetric={questionMetric}
                question={questions.find(a => a.questionId == questionMetric.questionId)}
                questionColorCounts={questionColorCounts}
                enabledLevels={enabledLevels}
                showAverageScore={showAverageScore}
                showDegreeOfAlignment={showDegreeOfAlignment}
                getClassFromScore={getClassFromScore}
                getClassFromAlignment={getClassFromAlignment}
                getColorFromAlignmentRank={getColorFromAlignmentRank}
                setPageView={changePageView}
              />
            }
            { pageView !== "overall" && commentMetric && commentMetric.comments.length > 0 && (
              <div>
                <a className="comment-button" onClick={onCommentsClick}>
                  <span><FontAwesomeIcon icon={faComments} size="2x" /></span>
                </a>
                <Offcanvas show={showComments} onHide={() => setShowComments(false)} placement="end" className="w-50">
                  <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{commentMetric.questionGroup} Comments</Offcanvas.Title>
                  </Offcanvas.Header>
                  <Offcanvas.Body>
                    <p className="fw-bold">{commentMetric.questionName} {commentQuestion && commentQuestion.title}</p>
                    {/* <Accordion defaultActiveKey="0">
                      { commentMetric.comments.map((comment, index) => (
                          <Accordion.Item key={index} eventKey={index}>
                            <Accordion.Header>
                              <p className="ellipsis align-middle w-75 mt-0 mb-0 mr-2">{comment}</p>
                            </Accordion.Header>
                            <Accordion.Body>{comment}</Accordion.Body>
                          </Accordion.Item>
                      ))}
                    </Accordion> */}

                    <ListGroup>
                      { commentMetric.comments.filter(a => a).map((comment, index) => (
                        <ListGroupItemEllipsis key={index} text={comment} enabledByDefault={false} />
                      ))}
                    </ListGroup>
                  </Offcanvas.Body>
                </Offcanvas>
              </div>
            )}
            { pageView !== "overall" && (
              <div className="mt-5 tile-container">
                <span className="flex p-2 text-center-middle" style={{alignItems: 'center', gap: '0.5rem'}}>Suboptimal<FontAwesomeIcon icon={faCircleMinus} className="fg-red" size="2x" /></span>
                <span className="p-4 bg-red" />
                <span className="p-4 bg-redOrange" />
                <span className="p-4 bg-orange" />
                <span className="p-4 bg-orangeYellow" />
                <span className="p-4 bg-yellow" />
                <span className="p-4 bg-yellowGreen" />
                <span className="p-4 bg-green" />
                <span className="p-4 bg-lightGreen" />
                <span className="flex p-2 text-center-middle" style={{alignItems: 'center', gap: '0.5rem'}}><FontAwesomeIcon icon={faCircleCheck} className="fg-lightGreen" size="2x" /> Optimal</span>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}